<template>
  <div class="scope-switcher" v-if="scopeButtons.length">
    <button
      v-for="b in scopeButtons"
      :key="b.id ?? 'all'"
      class="scope-button"
      :aria-pressed="b.active"
      @click="select(b.id)"
    >{{ b.label }}</button>
  </div>
  <div v-else class="scope-switcher" aria-hidden="true">
    <button class="scope-button" :aria-pressed="true">Personal</button>
  </div>
</template>

<script setup lang="ts">
// Sidebar scope switcher — replaces the old GlobalScopeFilter dropdown.
// Wraps useGlobalScopeFilter so toggling the buttons drives the same global
// filter every list/chart reads from.
//
// We stack the buttons vertically (instead of using the horizontal `.seg`
// primitive) because long household names like "Nicole + Janusz" overflow
// the 220px sidebar when laid out side-by-side.
import { computed, onMounted, watch } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useGlobalScopeFilter } from '@/composables/useGlobalScopeFilter'

const auth = useAuth() as any
const {
  globalAvailableScopes,
  globalSelectedScopes,
  setGlobalSelectedScopes,
  ensureAvailableScopes,
} = useGlobalScopeFilter()

// Load scopes once the user is authenticated.
//
// ensureAvailableScopes retries internally. The previous version called the
// bare loader and swallowed the rejection, and the only thing that would try
// again was another isAuthenticated transition — which doesn't come. One
// failed call at boot therefore left the switcher empty for the whole
// session, which is exactly what happens when the Auth0 token grant is still
// in flight as the shell mounts.
const loadOnceAuthed = () => {
  if (auth?.isAuthenticated?.value && globalAvailableScopes.value.length === 0) {
    void ensureAvailableScopes(auth)
  }
}
onMounted(loadOnceAuthed)
watch(() => auth?.isAuthenticated?.value, loadOnceAuthed)

interface ScopeButton {
  id: number | null
  label: string
  active: boolean
}

const scopeButtons = computed<ScopeButton[]>(() => {
  const scopes = globalAvailableScopes.value
  if (!scopes.length) return []

  // Single scope (most users): just show it as a static pressed button.
  if (scopes.length === 1) {
    return [{
      id: scopes[0].id,
      label: prettify(scopes[0]),
      active: true,
    }]
  }

  // Multi-scope users get "All" + one button per scope.
  const allSelected =
    globalSelectedScopes.value.length === scopes.length
  return [
    { id: null, label: 'All', active: allSelected },
    ...scopes.map(s => ({
      id: s.id,
      label: prettify(s),
      active:
        !allSelected &&
        globalSelectedScopes.value.length === 1 &&
        globalSelectedScopes.value[0] === s.id,
    })),
  ]
})

function prettify(scope: { name?: string; type?: string }): string {
  if (scope.type === 'personal') return 'Personal'
  if (scope.type === 'household') return scope.name || 'Joint'
  return scope.name || 'Scope'
}

function select(id: number | null) {
  const scopes = globalAvailableScopes.value
  if (id === null) {
    setGlobalSelectedScopes(scopes.map(s => s.id))
  } else {
    setGlobalSelectedScopes([id])
  }
}
</script>

<style scoped>
/* Grouped in its own box, as before — a filled well rather than an outlined
   one, so it matches the borderless language of the rest of the app. */
.scope-switcher {
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: var(--surface-2);
  border: 0;
  border-radius: 9px;
  padding: 3px;
  width: 100%;
}

.scope-button {
  width: 100%;
  text-align: left;
  padding: 6px 9px;
  font: inherit;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: -0.005em;
  color: var(--ink-3);
  background: transparent;
  border: 0;
  border-radius: 7px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.scope-button:hover:not([aria-pressed="true"]) {
  background: color-mix(in oklab, var(--ink) 7%, transparent);
  color: var(--ink-2);
}
/* Matches the active nav row: lifted surface + semibold. The selected pill has
   to sit ABOVE the well it lives in, and --surface only clears --surface-2 in
   light; dark needs the next step up. */
.scope-button[aria-pressed="true"] {
  background: var(--surface);
  color: var(--ink);
  font-weight: 600;
  box-shadow: var(--shadow-sm);
}
[data-theme="dark"] .scope-button[aria-pressed="true"],
.v-theme--dark .scope-button[aria-pressed="true"] {
  background: var(--surface-3);
}

/* Drawer mode: scope buttons join the 44px touch standard. */
@media (max-width: 880px) {
  .scope-switcher { border-radius: 10px; }
  .scope-button {
    padding: 12px 12px;
    font-size: 14px;
    border-radius: 8px;
  }
}
</style>
